import type { ReactNode } from 'react';
import { BRAND_ICONS, BRAND_ICON_SIZES, type BrandIconName } from './brandIcons.generated.js';
import { BRAND_MARKS } from './brandMarks.generated.js';

/**
 * RUNNER UI icons: the brand's 38-icon family (brand/exports/ui/icons), 24-unit grid, rounded caps and joins, two-unit
 * stroke. Each icon has 16/20/24/32 px optical variants (heavier stroke and less detail when small); the variant is
 * picked from the rendered size. Paint follows `currentColor`. Decorative unless given a title.
 */
type P = { size?: number; title?: string };

function opticalSize(size: number): (typeof BRAND_ICON_SIZES)[number] {
  return BRAND_ICON_SIZES.find((s) => size <= s) ?? 32;
}

function Svg({ size, title, viewBox = '0 0 24 24', children, body }: P & { size: number; viewBox?: string; children?: ReactNode; body?: string }) {
  return (
    <svg width={size} height={size} viewBox={viewBox} aria-hidden={title ? undefined : true} role={title ? 'img' : undefined} focusable="false">
      {title && <title>{title}</title>}
      {body ? <g dangerouslySetInnerHTML={{ __html: body }} /> : children}
    </svg>
  );
}

export function BrandIcon({ name, size = 22, title }: P & { name: BrandIconName }) {
  return <Svg size={size} title={title} body={BRAND_ICONS[name][opticalSize(size)]} />;
}

const brand = (name: BrandIconName) => (p: P) => <BrandIcon name={name} {...p} />;

export const IconHome = brand('home');
export const IconTrade = brand('trading');
export const IconRewards = brand('rewards');
export const IconHooks = brand('hooks');
export const IconJournal = brand('journal');
export const IconToken = brand('token');
export const IconVerify = brand('verified');
export const IconOperator = brand('settings');
export const IconShoe = brand('shoe');
export const IconWater = brand('water');
export const IconGate = brand('forge');
export const IconForge = brand('forge');
export const IconHarvest = brand('harvest');
export const IconFinish = brand('finish');
export const IconPause = brand('pause');
export const IconPlay = brand('play');
export const IconArrowRight = brand('buy-right');
export const IconArrowLeft = brand('sell-left');
export const IconCollectibles = brand('collectibles');
export const IconWallet = brand('wallet');
export const IconReplay = brand('replay');

/** Run: the three-stride motion emblem, which the brand reserves for movement (filled, so no optical variants). */
export const IconRun = ({ size = 22, title }: P) => (
  <svg width={size} height={size} viewBox="0 0 148 148" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined} focusable="false">
    {title && <title>{title}</title>}
    <g transform="translate(0 10)" dangerouslySetInnerHTML={{ __html: BRAND_MARKS.emblem.body }} />
  </svg>
);

/*
 * Encounters the brand family does not cover (fork, barrier, Overheat flame). Drawn on the same 24-unit grid with the
 * brand's stroke weights per optical size so they sit with the family.
 */
const STROKE: Record<number, number> = { 16: 2.35, 20: 2.2, 24: 2, 32: 2 };
function Local({ size = 22, title, children }: P & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={STROKE[opticalSize(size)]}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
    >
      {title && <title>{title}</title>}
      {children}
    </svg>
  );
}

export const IconFlame = (p: P) => (
  <Local {...p}>
    <path d="M12 21c-4 0-6.5-2.5-6.5-6 0-4 4-6 3.5-11 3 2 5 4 5 7 1-1 1.5-2 1.5-3 2 2 3 4.5 3 7 0 3.5-2.5 6-6.5 6z" />
  </Local>
);
export const IconFork = (p: P) => (
  <Local {...p}>
    <path d="M12 21v-7M12 14L6 6M12 14l6-8" />
    <path d="M4 7l2-1.5L7.5 8M20 7l-2-1.5L16.5 8" />
  </Local>
);
export const IconBarrier = (p: P) => (
  <Local {...p}>
    <rect x="3" y="8" width="18" height="6" rx="1" />
    <path d="M7 8l-3 6M12 8l-3 6M17 8l-3 6M21 8l-3 6M5 14v5M19 14v5" />
  </Local>
);
/** Puddle (engine v2): a flat splash. */
export const IconPuddle = (p: P) => (
  <Local {...p}>
    <path d="M4 15c0-2 3-3 5-2.6 1.6-1.8 5.6-1.8 7 .2 2.4-.2 4.2.8 4 2.6-.2 1.8-3 2.6-8 2.6S4 17 4 15z" />
    <path d="M9 9.5c.6-.8 1.4-.8 2 0M15 8c.5-.6 1.1-.6 1.6 0" />
  </Local>
);

export type { BrandIconName };
